/** Stage E — Opportunity Detail Drawer
 * Side-drawer that replaces full-page /proposal navigation for reviewing an
 * opportunity. Loads the composite payload from GET /opportunities/detail/{symbol}
 * and organises everything into 12 tabs.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, X, TrendingUp, TrendingDown, ExternalLink, RefreshCw, Play, CheckCircle2, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { keys } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import { invalidate } from "@/lib/queryInvalidate";
import WhyPanel from "./WhyPanel";
import LifecycleTimeline from "./LifecycleTimeline";
import NotesPanel from "./NotesPanel";
import ChartPanel from "./ChartPanel";
import TechnicalsPanel from "./TechnicalsPanel";
import { FundamentalsPanel, NewsPanel } from "./FundamentalsNewsPanels";
import MarketMemoryPanel from "./MarketMemoryPanel";
import OpportunityLifecycleTimeline from "./lifecycle/OpportunityLifecycleTimeline";
import ConvictionTrendChart from "./lifecycle/ConvictionTrendChart";

const TABS = [
  { key: "overview",   label: "Overview" },
  { key: "why",        label: "Why" },
  { key: "debate",     label: "AI Debate" },
  { key: "risk",       label: "Risk" },
  { key: "chart",      label: "Chart" },
  { key: "fundamentals", label: "Fundamentals" },
  { key: "technical",  label: "Technical" },
  { key: "news",       label: "News" },
  { key: "memory",     label: "Memory" },
  { key: "history",    label: "History" },
  { key: "plan",       label: "Trade Plan" },
  { key: "related",    label: "Related" },
  { key: "notes",      label: "Notes" },
];

function fmt(n, d = 2) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
}

function VerdictBadge({ v }) {
  if (!v) return null;
  const cls = /BUY/.test(v) ? "pnl-up" :
              /SELL|REDUCE/.test(v) ? "pnl-down" :
              /WATCH|HOLD/.test(v) ? "text-signal-yellow" : "text-foreground";
  return <span className={`chip !text-[10px] border-current ${cls}`}>{v.replace(/_/g, " ")}</span>;
}

function Row({ label, value, tone }) {
  return (
    <div className="grid grid-cols-2 gap-2 py-1 text-xs border-b border-border/30">
      <span className="text-muted-foreground uppercase tracking-wide text-[10px]">{label}</span>
      <span className={`mono text-right ${tone || ""}`}>{value}</span>
    </div>
  );
}

export default function OpportunityDrawer({ open, symbol, onClose, initialTab = "overview" }) {
  const nav = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [refreshing, setRefreshing] = useState(false);
  const [visited, setVisited] = useState(() => new Set([initialTab]));
  const qc = useQueryClient();

  const upperSym = symbol?.toUpperCase();

  const {
    data,
    isLoading: loading,
  } = useQuery({
    queryKey: upperSym ? keys.opportunityDetail(upperSym) : ["opportunity-detail", "none"],
    queryFn: async () => {
      const r = await api.get(`/opportunities/detail/${upperSym}`);
      return r.data;
    },
    enabled: !!(open && upperSym),
    staleTime: STALE.SHORT,
    gcTime: GC.SHORT,
  });

  // Reset tab & visited set whenever we open a different symbol.
  useEffect(() => {
    if (open && upperSym) {
      setTab(initialTab);
      setVisited(new Set([initialTab]));
    }
  }, [open, upperSym, initialTab]);

  // Track which tabs the user has visited so heavy panels can stay mounted
  // (cache remains warm) while never mounting a panel before it is opened.
  const onTabChange = useCallback((v) => {
    setTab(v);
    setVisited((prev) => {
      if (prev.has(v)) return prev;
      const next = new Set(prev);
      next.add(v);
      return next;
    });
  }, []);
  const wasVisited = (k) => visited.has(k);

  const refresh = async () => {
    if (!upperSym) return;
    setRefreshing(true);
    try {
      const beforeId = analysis?.id || null;
      const { data } = await api.post("/analysis/refresh", { symbol: upperSym });
      if (data.duplicate) {
        toast.info(`Analysis for ${upperSym} already running`);
      } else {
        toast.success(`Analysis queued for ${upperSym}`);
      }
      // Kick the active-job query so the progress panel appears immediately.
      qc.invalidateQueries({ queryKey: ["analysis", "job", upperSym] });

      // Own the hot-swap. We can't rely on the job-progress query to fire
      // `jobJustCompleted` reliably (fast refreshes finish before the
      // poller catches them; slow ones may race the useEffect). Poll the
      // detail endpoint directly, up to 90s @ 1.5s, until we see either:
      //   (a) a NEW analysis id (id !== beforeId), OR
      //   (b) analyst/critic populated on the same id (in-place update).
      // Then auto-switch to AI Debate + toast with Open Full action.
      (async () => {
        const deadline = Date.now() + 90_000;
        while (Date.now() < deadline) {
          const r = await qc.fetchQuery({
            queryKey: keys.opportunityDetail(upperSym),
            queryFn: async () => (await api.get(`/opportunities/detail/${upperSym}`)).data,
            staleTime: 0,
          }).catch(() => null);
          const a = r?.analysis;
          const isFresh = a && ((beforeId && a.id !== beforeId) || a.analyst || a.critic);
          if (isFresh) {
            invalidate.onAnalysisRefreshed(upperSym);
            setTab("debate");
            setVisited((prev) => new Set([...prev, "debate"]));
            toast.success(`Analysis ready for ${upperSym}`, a.id ? {
              action: { label: "Open Full", onClick: () => nav(`/proposal/${a.id}`) },
            } : undefined);
            return;
          }
          await new Promise((res) => setTimeout(res, 1500));
        }
        toast.info(`Analysis for ${upperSym} is taking longer than expected — check Analysis History if it doesn't appear.`);
      })();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Run failed");
    } finally { setRefreshing(false); }
  };

  // Poll for an active analysis job for this symbol. Polling stops as soon as
  // the job reaches a terminal state (completed / failed / cancelled).
  const { data: activeJobResp } = useQuery({
    queryKey: ["analysis", "job", upperSym],
    queryFn: async () => (await api.get(`/analysis/jobs/active?symbol=${upperSym}`)).data,
    enabled: !!(open && upperSym),
    refetchInterval: (q) => {
      const j = q.state.data?.active_job;
      if (!j) return false;
      return ["completed", "failed", "cancelled"].includes(j.status) ? false : 1500;
    },
    staleTime: 0,
  });
  const job = activeJobResp?.active_job || null;
  const jobActive = job && !["completed", "failed", "cancelled"].includes(job.status);
  const jobJustCompleted = job?.status === "completed";

  // When a job completes, hot-swap analysis into the drawer.
  // The backend writes the AnalysisResult a moment after the job row flips
  // to `completed`, so we poll `opportunityDetail` a few times until the
  // analyst/critic block actually lands. Then we auto-switch to the AI
  // Debate tab so the user sees the newly-generated content immediately
  // (previously the drawer stayed on Overview and looked frozen), and we
  // fire a toast with a one-tap link to "Open full" — fixing the
  // complaint that Open Full asked to run analysis again because the
  // drawer still had the stale (null-analysis) payload.
  useEffect(() => {
    if (!jobJustCompleted || !upperSym) return;
    let cancelled = false;
    let toastFired = false;
    (async () => {
      invalidate.onAnalysisRefreshed(upperSym);
      // Up to 6 tries × 700ms = ~4s window for analyst/critic to land.
      for (let i = 0; i < 6 && !cancelled; i++) {
        const r = await qc.fetchQuery({
          queryKey: keys.opportunityDetail(upperSym),
          queryFn: async () => (await api.get(`/opportunities/detail/${upperSym}`)).data,
          staleTime: 0,
        }).catch(() => null);
        const a = r?.analysis;
        if (a && (a.analyst || a.critic)) {
          if (!toastFired) {
            toastFired = true;
            if (a.id) {
              toast.success(`Analysis ready for ${upperSym}`, {
                action: {
                  label: "Open Full",
                  onClick: () => nav(`/proposal/${a.id}`),
                },
              });
            } else {
              toast.success(`Analysis ready for ${upperSym}`);
            }
            setTab("debate");
            setVisited((prev) => new Set([...prev, "debate"]));
          }
          break;
        }
        await new Promise((res) => setTimeout(res, 700));
      }
    })();
    return () => { cancelled = true; };
  }, [jobJustCompleted, upperSym, qc, nav]);

  const opp = data?.opportunity;
  const analysis = data?.analysis;
  const hasFullAnalysis = !!(analysis && (analysis.analyst || analysis.critic));
  const why = data?.why;
  const analyst = analysis?.analyst || {};
  const critic = analysis?.critic || {};
  const meta = analysis?.analysis_meta || {};
  const plans = analyst?.plans || [];
  const rec = plans.find(p => p.id === critic?.recommended_plan_id) || plans[0];
  const oppId = opp?.id;
  const lifecycleEvents = data?.opp_lifecycle || [];
  const scoreHistory = data?.score_history || [];

  // Fire VIEWED once per opportunity, when the drawer opens. Backend is
  // idempotent (only records if current state == NEW) — this is a background
  // best-effort ping.
  useEffect(() => {
    if (!open || !oppId) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      api.post(`/opportunities/${oppId}/view`).then((r) => {
        if (r?.data?.recorded) {
          qc.invalidateQueries({ queryKey: keys.opportunityDetail(upperSym) });
        }
      }).catch(() => {});
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, oppId, upperSym, qc]);

  const changePct = opp?.change_pct;
  const up = (changePct ?? 0) >= 0;
  const verdict = opp?.verdict || meta.verdict;

  const goProposal = () => {
    if (analysis?.id) { nav(`/proposal/${analysis.id}`); return; }
    // No analysis yet — offer to run it now instead of a dead toast.
    toast.info(`No full analysis yet for ${symbol}`, {
      action: { label: "Run Full Analysis", onClick: () => refresh() },
    });
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl h-full sm:h-auto overflow-hidden p-0 flex flex-col" data-testid="opportunity-drawer">
        <SheetHeader className="p-4 border-b border-border/60 space-y-2">
          <div className="flex items-center gap-3">
            <SheetTitle className="text-xl mono flex items-baseline gap-3">
              <span data-testid="drawer-symbol">{symbol?.toUpperCase()}</span>
              {opp?.name && opp.name.toUpperCase() !== (opp.symbol || "").toUpperCase() && (
                <span className="text-xs text-muted-foreground font-normal">{opp.name}</span>
              )}
            </SheetTitle>
            <SheetDescription className="sr-only">Detailed AI analysis, risk, plans, and lifecycle for {symbol}</SheetDescription>
            <div className="ml-auto flex items-center gap-1">
              <button onClick={refresh} disabled={refreshing || jobActive} data-testid="drawer-refresh" className="btn-ghost h-7 !px-2 text-[10px]">
                {refreshing || jobActive ? <Loader2 size={11} className="animate-spin"/> :
                    hasFullAnalysis ? <RefreshCw size={11}/> : <Play size={11}/>}
                {jobActive ? "Analysis Running…" : hasFullAnalysis ? "Refresh Analysis" : "Run Full Analysis"}
              </button>
              <button onClick={goProposal} data-testid="drawer-open-full" className="btn-ghost h-7 !px-2 text-[10px]">
                <ExternalLink size={11}/> Open full
              </button>
              <button onClick={onClose} aria-label="Close" className="btn-ghost h-7 !px-2 text-[10px]" data-testid="drawer-close">
                <X size={12}/>
              </button>
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-2 text-xs">
            <VerdictBadge v={verdict}/>
            {opp?.price !== undefined && (
              <span className="mono">${fmt(opp.price)}</span>
            )}
            {changePct !== undefined && changePct !== null && (
              <span className={`mono ${up ? "pnl-up" : "pnl-down"} flex items-center gap-0.5`}>
                {up ? <TrendingUp size={11}/> : <TrendingDown size={11}/>}
                {up ? "+" : ""}{fmt(changePct)}%
              </span>
            )}
            {opp?.sector && <span className="chip !text-[10px] border-current text-muted-foreground">{opp.sector}</span>}
            {opp?.score !== undefined && <span className="mono text-[10px] text-muted-foreground">score {opp.score}</span>}
            {opp?.risk_score !== undefined && <span className="mono text-[10px] text-muted-foreground">risk {opp.risk_score}</span>}
            {opp?.confidence !== undefined && <span className="mono text-[10px] text-muted-foreground">conf {opp.confidence}</span>}
          </div>
        </SheetHeader>

        <Tabs value={tab} onValueChange={onTabChange} className="flex-1 flex flex-col overflow-hidden">
          {(jobActive || job?.status === "failed") && (
            <div className={`px-3 py-2 border-b border-border/60 text-[11px] ${job?.status === "failed" ? "bg-signal-red/10 border-signal-red/30" : "bg-secondary/30"}`}
                 data-testid="drawer-job-panel">
              {jobActive && (
                <div className="flex items-center gap-2">
                  <Loader2 size={11} className="animate-spin text-signal-blue"/>
                  <span className="mono uppercase text-[10px] text-signal-blue">
                    {(job.current_stage || job.status || "queued").replace(/_/g, " ")}
                  </span>
                  <span className="ml-auto mono text-[10px] text-muted-foreground">
                    {job.progress_pct != null ? `${job.progress_pct}%` : "…"}
                  </span>
                </div>
              )}
              {jobActive && job.progress_pct != null && (
                <div className="mt-1.5 h-1 bg-secondary/40 relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-signal-blue transition-all duration-300" style={{ width: `${job.progress_pct}%` }}/>
                </div>
              )}
              {job?.status === "failed" && (
                <div className="flex items-center gap-2">
                  <AlertTriangle size={11} className="text-signal-red"/>
                  <span className="mono uppercase text-[10px] text-signal-red">Analysis failed</span>
                  <span className="text-muted-foreground truncate">{job.error_message || "Unknown error"}</span>
                  <button onClick={refresh} className="btn-outline h-5 !px-2 text-[10px] ml-auto" data-testid="drawer-job-retry">Retry</button>
                </div>
              )}
            </div>
          )}
          <TabsList className="w-full justify-start rounded-none border-b border-border/60 bg-transparent h-9 px-2 overflow-x-auto flex-shrink-0">
            {TABS.map(t => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                data-testid={`drawer-tab-${t.key}`}
                className="mono uppercase text-[10px] tracking-wide data-[state=active]:bg-secondary data-[state=active]:text-foreground h-7 px-2 rounded-none"
              >{t.label}</TabsTrigger>
            ))}
          </TabsList>

          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="drawer-body">
            {loading && !data && (
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 size={12} className="animate-spin"/> Loading…
              </div>
            )}

            <TabsContent value="overview" className="space-y-3 mt-0">
              <Row label="Verdict" value={verdict || "—"}/>
              <Row label="Score" value={opp?.score ?? meta.opportunity_score ?? "—"}/>
              <Row label="Risk score" value={opp?.risk_score ?? meta.risk_score ?? "—"}/>
              <Row label="Confidence" value={opp?.confidence ?? meta.confidence ?? "—"}/>
              <Row label="Suggested portfolio" value={opp?.suggested_portfolio || meta.suggested_portfolio || "—"}/>
              <Row label="Category" value={opp?.category || meta.category || "—"}/>
              <Row label="Holding period" value={opp?.holding_period || meta.holding_period || "—"}/>
              <Row label="Reward / Risk" value={fmt(opp?.reward_risk ?? rec?.reward_risk ?? rec?.rr, 2)}/>
              <Row label="Entry" value={rec?.entry != null ? `$${fmt(rec.entry)}` : (opp?.suggested_entry ? `$${fmt(opp.suggested_entry)}` : "—")}/>
              <Row label="Stop" value={rec?.stop != null ? `$${fmt(rec.stop)}` : (opp?.suggested_stop ? `$${fmt(opp.suggested_stop)}` : "—")}/>
              <Row label="Target" value={rec?.target != null ? `$${fmt(rec.target)}` : (opp?.suggested_target ? `$${fmt(opp.suggested_target)}` : "—")}/>
              {opp?.analyst_summary && (
                <div className="border border-border/40 p-2 mt-2">
                  <div className="label-cap mb-1">Analyst summary</div>
                  <p className="text-xs text-foreground/85 leading-relaxed whitespace-pre-wrap">{opp.analyst_summary}</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="why" className="mt-0">
              <WhyPanel why={why}/>
            </TabsContent>

            <TabsContent value="debate" className="mt-0 space-y-3">
              {analyst?.thesis && (
                <div className="border border-border/40 p-2">
                  <div className="label-cap mb-1">Analyst thesis</div>
                  <p className="text-xs text-foreground/85 leading-relaxed whitespace-pre-wrap">{analyst.thesis}</p>
                </div>
              )}
              {(analyst?.supporting_evidence || []).length > 0 && (
                <div className="border border-border/40 p-2">
                  <div className="label-cap mb-1">Supporting evidence</div>
                  <ul className="text-xs space-y-1">{analyst.supporting_evidence.map((e, i) => <li key={i} className="pl-3 relative before:content-['·'] before:absolute before:left-0">{e}</li>)}</ul>
                </div>
              )}
              {(critic?.portfolio_concerns || []).length > 0 && (
                <div className="border border-border/40 p-2">
                  <div className="label-cap mb-1 text-signal-red">Critic concerns</div>
                  <ul className="text-xs space-y-1">{critic.portfolio_concerns.map((e, i) => <li key={i} className="pl-3 relative before:content-['·'] before:absolute before:left-0">{e}</li>)}</ul>
                </div>
              )}
              {!analyst?.thesis && !critic && (
                <div className="border border-dashed border-border/40 p-3 space-y-2">
                  <div className="text-xs text-muted-foreground">
                    {jobActive
                      ? "AI debate is running — this tab will populate the moment the analyst and critic finish."
                      : analysis
                        ? "This opportunity has a shallow analysis (score only). Click Run Full Analysis above to generate the analyst and critic debate."
                        : "No full analysis yet. Click Run Full Analysis above to trigger the AI debate."}
                  </div>
                  {!jobActive && (
                    <button onClick={refresh} data-testid="debate-run-cta"
                            className="text-[10px] mono uppercase tracking-widest border border-signal-blue text-signal-blue px-2 py-1 hover:bg-signal-blue/10">
                      {analysis ? "Refresh Analysis" : "Run Full Analysis"}
                    </button>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="risk" className="mt-0 space-y-2">
              {data?.hysteresis && (
                <div className="border border-border/40 p-2">
                  <div className="label-cap mb-1">Recommendation stability</div>
                  <div className="text-xs">
                    <div>Effective: <span className="mono">{data.hysteresis.effective_verdict}</span></div>
                    <div>Candidate: <span className="mono">{data.hysteresis.candidate_verdict}</span></div>
                    <div>Locked: <span className="mono">{data.hysteresis.verdict_locked ? "yes" : "no"}</span></div>
                    <ul className="mt-1 space-y-0.5">
                      {(data.hysteresis.reasons || []).map((r, i) => (
                        <li key={i} className="pl-3 relative before:content-['·'] before:absolute before:left-0 text-foreground/80">{r.label || r.code}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {(opp?.risks || []).length > 0 && (
                <div className="border border-border/40 p-2">
                  <div className="label-cap mb-1">Risks</div>
                  <ul className="text-xs space-y-1">{opp.risks.map((r, i) => <li key={i} className="pl-3 relative before:content-['·'] before:absolute before:left-0">{r}</li>)}</ul>
                </div>
              )}
            </TabsContent>

            <TabsContent value="chart" className="mt-0 space-y-2">
              {!hasFullAnalysis && (
                <div className="border border-border/40 p-2 text-[11px] text-muted-foreground bg-secondary/20" data-testid="drawer-preliminary">
                  Preliminary scanner setup. Levels shown below come from the scanner pre-screen — click <span className="mono">Run Full Analysis</span> to generate the Analyst, Critic, and validated trade plans.
                </div>
              )}
              {wasVisited("chart") && (
                <ChartPanel
                  symbol={symbol}
                  entry={opp?.suggested_entry ?? rec?.entry}
                  stop={opp?.suggested_stop ?? rec?.stop}
                  target={opp?.suggested_target ?? rec?.target}
                />
              )}
            </TabsContent>

            <TabsContent value="fundamentals" className="mt-0">
              {wasVisited("fundamentals") && <FundamentalsPanel symbol={symbol}/>}
            </TabsContent>

            <TabsContent value="technical" className="mt-0">
              {wasVisited("technical") && <TechnicalsPanel symbol={symbol}/>}
            </TabsContent>

            <TabsContent value="news" className="mt-0">
              {wasVisited("news") && <NewsPanel symbol={symbol} decisionDate={analysis?.completed_at}/>}
            </TabsContent>

            <TabsContent value="memory" className="mt-0">
              {wasVisited("memory") && <MarketMemoryPanel symbol={symbol}/>}
            </TabsContent>

            <TabsContent value="history" className="mt-0 space-y-4" data-testid="history-tab-content">
              {/* 1. Opportunity Lifecycle */}
              <details open className="group" data-testid="history-section-lifecycle">
                <summary className="cursor-pointer sm:cursor-default list-none flex items-center justify-between sm:pointer-events-none">
                  <span className="label-cap">Opportunity Story</span>
                  <span className="sm:hidden mono text-[10px] text-muted-foreground group-open:hidden">tap to expand</span>
                </summary>
                <div className="mt-2">
                  <OpportunityLifecycleTimeline
                    events={lifecycleEvents}
                    isLegacy={!!(opp && (opp.created_at || "") < "2026-02-03")}
                    opportunityCreatedAt={opp?.created_at}
                  />
                </div>
              </details>

              {/* 2. Conviction Trend */}
              <details open className="group" data-testid="history-section-conviction">
                <summary className="cursor-pointer sm:cursor-default list-none flex items-center justify-between sm:pointer-events-none">
                  <span className="label-cap">Conviction Over Time</span>
                  <span className="sm:hidden mono text-[10px] text-muted-foreground group-open:hidden">tap to expand</span>
                </summary>
                <div className="mt-2">
                  <ConvictionTrendChart
                    history={scoreHistory}
                    lifecycleEvents={lifecycleEvents}
                  />
                </div>
              </details>

              {/* 3. Existing analysis/version history (unchanged) */}
              <details open className="group" data-testid="history-section-analysis">
                <summary className="cursor-pointer sm:cursor-default list-none flex items-center justify-between sm:pointer-events-none">
                  <span className="label-cap">Analysis History</span>
                  <span className="sm:hidden mono text-[10px] text-muted-foreground group-open:hidden">tap to expand</span>
                </summary>
                <div className="mt-2">
                  <LifecycleTimeline entries={data?.timeline || []} currentState={opp?.lifecycle_state || analysis?.lifecycle_state}/>
                  {(data?.events || []).length > 0 && (
                    <div className="mt-3">
                      <div className="label-cap mb-2">Recent decisions</div>
                      <div className="space-y-1">
                        {data.events.slice(0, 10).map(e => (
                          <div key={e.id} className="border border-border/30 p-1.5 text-[11px] flex items-baseline gap-2" data-testid={`event-${e.event_type}`}>
                            <span className="chip !text-[9px] border-current">{e.actor}</span>
                            <span className="mono">{e.event_type}</span>
                            <span className="mono text-[10px] text-muted-foreground ml-auto">{new Date(e.created_at).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </details>
            </TabsContent>

            <TabsContent value="plan" className="mt-0 space-y-2">
              {plans.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No plans yet — refresh the analysis to generate plans.</p>
              ) : (
                plans.map(p => (
                  <div key={p.id} className={`border p-2 ${p.id === critic?.recommended_plan_id ? "border-signal-blue" : "border-border/40"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="mono text-sm">Plan {p.id}</span>
                      {p.id === critic?.recommended_plan_id && <span className="chip pnl-up border-current !text-[10px]">Recommended</span>}
                      <span className="ml-auto mono text-[10px] text-muted-foreground">R/R {fmt(p.reward_risk || p.rr, 2)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[11px] mono">
                      <div>Entry: ${fmt(p.entry)}</div>
                      <div>Stop: ${fmt(p.stop)}</div>
                      <div>Target: ${fmt(p.target)}</div>
                    </div>
                    {p.rationale && <p className="text-[11px] mt-1 text-foreground/80">{p.rationale}</p>}
                  </div>
                ))
              )}
              <button
                onClick={goProposal}
                disabled={!analysis?.id}
                className="w-full mono uppercase text-[11px] tracking-wider border border-signal-blue py-2 mt-2 hover:bg-signal-blue/10 disabled:opacity-40"
                data-testid="drawer-goto-proposal"
              >
                Open in Manual Approval →
              </button>
            </TabsContent>

            <TabsContent value="related" className="mt-0">
              {(data?.related || []).length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No related opportunities in the same sector.</p>
              ) : (
                <div className="space-y-1">
                  {data.related.map(r => (
                    <button
                      key={r.id}
                      onClick={() => { window.dispatchEvent(new CustomEvent("tm:open-drawer", { detail: { symbol: r.symbol } })); }}
                      className="w-full border border-border/40 p-2 text-left hover:bg-secondary/40 transition-colors"
                      data-testid={`related-${r.symbol}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="mono text-sm">{r.symbol}</span>
                        <VerdictBadge v={r.verdict}/>
                        <span className="ml-auto mono text-[10px]">score {r.score}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{r.name}</div>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="notes" className="mt-0">
              <NotesPanel entityType="symbol" entityId={symbol?.toUpperCase()} symbol={symbol}/>
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
